/**
 * Product Detail Page Theme
 * Styles for product detail page components
 */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';

const PDP_RAIL_WIDTH_LG = 'min(360px, 41.6667%)';

const ProductDetail = {
  parts: [
    'productView',
    'galleryContainer',
    'belowGallerySection',
    'productTabsInformationContainer',
    'productTabsInformationTab',
    'productTabsInformationTabActive',
    'productTabsInformationTabInactive',
    'productTabsInformationTabHidden',
    'productTabsInformationTabText',
    'productName',
    'productLongDescription',
    'productViewWrapper',
    'specificationsContainer',
    'specificationsInnerBox',
    'specificationsContainerLeft',
    'specificationsContainerLeftDimensionimage',
    'specificationsHeader',
    'specificationsHeaderDimensionimage',
    'specificationsTitle',
    'specificationsExpandAllButton',
    'specificationsExpandAllText',
    'specificationsExpandAllIconBox',
    'specificationsAccordion',
    'specificationsAccordionItem',
    'specificationsAccordionButton',
    'specificationsAccordionPanel',
    'specificationsAccordionContent',
    'specificationsAccordionContentProductSupport',
    'productSupportLink',
    'specificationsSectionTitle',
    'specificationsIconBox',
    // New parts for inline styles
    'configPanel',
    'mainFlexContainer',
    'mobileBaseInfoSection',
    'desktopBaseInfoSection',
    'baseInformationStack',
    'baseInformationProductPackage',
    'baseInformationProductInfo',
    'baseInformationTitle',
    'baseInformationShortDescriptionSkeleton',
    'baseInformationShortDescription',
    'baseInformationShortDescriptionText',
    'baseInformationDescription',
    'baseInformationRatings',
    'variationsSection',
    'tabsContainer',
    'quantityLabel',
    'fullDetailsLink',
    'promotionAccordion',
    'promotionAccordionItem',
    'finishedGoodPromotion',
    'promotionAccordionButton',
    'promotionButtonInner',
    'promotionContent',
    'promotionDot',
    'promotionTitle',
    'promotionToggleBox',
    'promotionToggleIcon',
    'promotionAccordionPanel',
    'errorMessage',
    'stockIndicatorFlex',
    'stockIndicatorBox',
    'stockIndicatorBreathingDot',
    'stockIndicatorSolidDot',
    'stockIndicatorText',
    'outOfStockIndicatorFlex',
    'outOfStockIndicatorBox',
    'outOfStockIndicatorDot',
    'outOfStockIndicatorText',
    'notifyMeButton',
    'pdpPriceSection',
    'pdpPaymentMethods',
    'totalPriceInfo',
    'totalPriceRow',
    'totalPriceDetails',
    'totalPriceLabel',
    'totalPriceValue',
    'totalPriceSavingBadge',
    'totalPricePackageInfo',
    'totalPriceListValue',
    'totalPriceListValueAmount',
    'totalPriceDeposit',
    'totalPricePaymentLogos',
    'inventoryMessage',
    'termsAndConditions',
    'stickyActionButtons',
    'actionButton',
    'wishlistButton',
    'specificationsDimensionImageBox',
    'addToCartStripDesktop',
    'addToCartStripDesktopInner',
    'addToCartStripMobile',
    'addToCartStripMobileInner',
    'addToCartStripProductName',
    'addToCartStripOptions',
    'addToCartStripPrice',
    'addToCartStripButton',
    'addToCartStripMobileTextBlock',
    'addToCartStripMobileProductName',
    'addToCartStripMobilePrice',
    'addToCartStripMobilePriceRowWithChevron',
    'addToCartStripMobilePriceRemainder',
    'addToCartStripMobilePriceChevronCluster',
    'addToCartStripMobilePriceInCluster',
    'addToCartStripMobileFromPrefix',
    'addToCartStripChevron',
    'productActionSectionContent',
    'productActionSection',
    'productActionSectionMessaging',
  ],

  baseStyle: {
    productView: {
      maxWidth: 'container.xxl',
      width: '100%',
      marginX: 'auto',
      paddingX: {base: 5, lg: 20},
      paddingTop: {base: 6, lg: 12},
      paddingBottom: {base: 12, lg: 20},
      alignItems: {base: 'stretch', lg: 'flex-start'},
      direction: {base: 'column', lg: 'row'},
      gap: {base: 6, lg: 20},
    },

    galleryContainer: {
      alignSelf: {lg: 'flex-start'},
      flex: {base: 1, lg: '1 1 0%'},
      flexBasis: {base: '100%', lg: 0},
      maxWidth: {base: '100%', lg: '100%'},
      minWidth: {base: 0, lg: 0},
      position: {base: 'static', lg: 'sticky'},
      top: {lg: 'var(--header-height, 0px)'},
      width: {base: '100%', lg: 'auto'},
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 0, lg: 2},
      sx: {
        '& > div': {
          width: {base: 'calc(100% + 40px)', lg: '100%'},
          marginLeft: {base: -5, lg: 0},
        },
      },
    },

    belowGallerySection: {
      minWidth: 0,
      width: '100%',
      sx: {
        '&:has(.swiper-button-lock) .view-details-container': {
          marginLeft: {base: 0, lg: '-12px'},
        },
      },
    },

    productTabsInformationContainer: {
      alignItems: 'stretch',
      display: 'flex',
      width: '100%',
      borderBottom: '2px solid',
      borderColor: 'brandColors.greyscale10',
    },

    productTabsInformationTab: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: {base: 2.875, lg: 2.875},
      justifyContent: 'center',
      marginBottom: '-2px',
      paddingY: '14px',
      paddingX: {base: '10px', lg: '16px'},
      position: 'relative',
      textAlign: 'center',
    },

    productTabsInformationTabActive: {
      _after: {
        bg: 'brandColors.primaryDarkLinen',
        bottom: 0,
        content: '""',
        height: '2px',
        left: 0,
        position: 'absolute',
        right: 0,
        transform: 'translateZ(0)',
        transition: 'background-color 0.2s ease',
        width: '100%',
        willChange: 'background-color',
      },
      sx: {
        '& p': {
          color: 'brandColors.schemesOnSurface',
        },
      }
    },

    productTabsInformationTabInactive: {
      _after: {
        bg: 'transparent',
        bottom: 0,
        content: '""',
        height: '2px',
        left: 0,
        position: 'absolute',
        right: 0,
        transform: 'translateZ(0)',
        transition: 'background-color 0.2s ease',
        width: '100%',
        willChange: 'background-color',
      },
      sx: {
        '& p': {
          color: 'greyscale.70',
        },
      }
    },

    productTabsInformationTabHidden: {
      opacity: 0,
      pointerEvents: 'none',
      position: 'relative',
      zIndex: -1,
    },

    productTabsInformationTabText: {
      fontFamily: 'heading',
      fontSize: 'md',
      fontWeight: 600,
      lineHeight: 1.2,
      textAlign: 'center',
      width: '100%',
    },

    productName: {
      color: 'brandColors.greyscale',
      fontFamily: 'heading',
      fontSize: {base: '2rem', lg: '2rem'},
      fontWeight: 500,
      lineHeight: 1.2,
    },

    productLongDescription: {
      color: 'brandColors.greyscale',
      fontFamily: 'body',
      fontSize: 'sm',
      fontWeight: 400,
      lineHeight: 1.4,
      // Nested element styles (applied via sx prop in component)
      nestedStyles: {
        '& p': {
          marginBottom: 4,
        },
        '& ul, & ol': {
          marginLeft: 4,
          marginBottom: 4,
        },
        '& li': {
          marginBottom: 2,
        },
        '& h3, & h4': {
          fontWeight: 'bold',
          marginTop: 4,
          marginBottom: 2,
        },
      },
    },

    productViewWrapper: {
      maxWidth: 'container.xxl',
      width: '100%',
      marginX: 'auto',
      paddingX: {base: 0, lg: 20},
    },

    productViewWrapperInner: {
      paddingX: {base: 5, lg: 0},
    },

    specificationsContainer: {
      bg: 'brandColors.primaryBG',
      marginX: 'auto',
      maxWidth: 'container.xxl',
      paddingTop: {base: 15, lg: 20},
      paddingBottom: {base: 0, lg: 30},
      paddingX: {base: 5, lg: 20},
      justifyContent: 'space-between',
      width: '100%',
      // Prefer scroll anchoring on this block when layout above shifts (delivery, etc.).
      sx: {overflowAnchor: 'auto'},
    },

    specificationsInnerBox: {
      flex: {base: 1, lg: 5},
    },

    specificationsContainerLeft: {
      flex: {base: 1, lg: '100%'},
      width: {base: '100%', lg: '100%'},
      minWidth: 0,
    },

    specificationsContainerLeftDimensionimage: {
      flex: {base: 1, lg: '1 1 51.5625%'},
      maxWidth: {base: '100%', lg: '51.5625%'},
    },

    specificationsHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: {base: 8, lg: 12},
    },

    specificationsHeaderDimensionimage: {
      marginBottom: {base: '0', lg: '7.5625rem'},
    },

    specificationsTitle: {
      fontFamily: 'heading',
      color: 'brandColors.primaryText',
      fontSize: {base: '2rem', lg: '2.5rem'},
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.4px',
    },

    specificationsExpandAllButton: {
      color: 'brandColors.primaryDarkLinen',
      height: {base: '8.5', lg: 12},
      paddingLeft: 0,
      paddingRight: 0,
      paddingX: 0,
      px: {
        base: 0,
        lg: 0,
      },
      _active: {
        bg: 'transparent',
      },
      _hover: {
        bg: 'transparent',
      },
    },

    specificationsExpandAllText: {
      color: 'brandColors.primaryDarkLinen',
      fontFamily: 'body',
      fontSize: {base: '0.8125rem', lg: '1.125rem'},
      fontWeight: 500,
      lineHeight: 1.2,
    },

    specificationsExpandAllIconBox: {
      alignItems: 'center',
      color: 'brandColors.primaryDarkLinen',
      display: 'inline-flex',
      justifyContent: 'center',
      height: {base: 3, lg: 5},
      width: {base: 3, lg: 5},
    },

    specificationsAccordion: {
      borderColor: 'transparent',
    },

    specificationsAccordionItem: {
      borderBottomWidth: 0,
      borderTopColor: 'brandColors.greyscale10',
      borderTopWidth: '1px',
      paddingTop: 6,
      paddingBottom: 6,
    },

    specificationsAccordionButton: {
      height: {base: 'auto', lg: 'auto'},
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      _hover: {
        bg: 'transparent',
      },
    },

    specificationsAccordionPanel: {
      fontSize: '0.8125rem',
      marginBottom: 0,
      marginTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },

    // Content inside accordion collapse
    specificationsAccordionContent: {
      color: 'text.primary',
      fontFamily: 'body',
      fontSize: {base: '1xs', lg: 'md'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'shorter',
      sx: {
        '& ul': {
          listStyleType: 'disc',
          listStylePosition: 'outside',
          paddingLeft: {base: 2, lg: 5},
          marginTop: 2,
          marginBottom: 4,
          paddingLeft: 5,
        },
        '& ol': {
          listStyleType: 'decimal',
          listStylePosition: 'outside',
          paddingLeft: {base: 2, lg: 5},
          marginTop: 2,
          marginBottom: 4,
          paddingLeft: 5,
        },
        '& *': {
          fontSize: {base: '1xs', lg: 'md'},
        },
        // DE-264 — preserve WYSIWYG bold/italic inside Feature overview HTML
        '& strong, & b': {
          fontWeight: 600,
        },
        '& em, & i': {
          fontStyle: 'italic',
        },
      }
    },

    /** pimlyProductSupport (BM) — container; links styled via productSupportLink. */
    specificationsAccordionContentProductSupport: {
      color: 'text.primary',
      fontFamily: 'body',
      fontSize: {base: '1xs', lg: 'md'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 'shorter',
      sx: {
        '& ul': {
          listStyleType: 'disc',
          listStylePosition: 'outside',
          paddingLeft: {base: 2, lg: 5},
          marginTop: 2,
          marginBottom: 4,
          paddingLeft: 5,
        },
        '& ol': {
          listStyleType: 'decimal',
          listStylePosition: 'outside',
          paddingLeft: {base: 2, lg: 5},
          marginTop: 2,
          marginBottom: 4,
          paddingLeft: 5,
        },
        '& *': {
          fontSize: {base: '1xs', lg: 'md'},
        },
      },
    },

    productSupportLink: {
      alignItems: 'center',
      color: 'rgba(50, 49, 47, 0.90)',
      display: 'inline-flex',
      fontWeight: 600,
      gap: '8px',
      lineHeight: 1.2,
      textDecoration: 'underline',
      textDecorationSkipInk: 'none',
      textUnderlineOffset: '2px',
      _hover: {
        color: 'rgba(50, 49, 47, 0.90)',
        textDecoration: 'underline',
      },
    },

    specificationsSectionTitle: {
      color: 'brandColors.greyscale',
      flex: '1',
      fontSize: 'lg',
      fontWeight: 500,
      lineHeight: 1.2,
      textAlign: 'left',
      minHeight: '1.375rem',
    },

    specificationsIconBox: {
      alignItems: 'center',
      color: 'brandColors.primaryDarkLinen',
      display: 'flex',
      height: 4,
      justifyContent: 'center',
      width: 4,
    },

    // Configuration Panel (Desktop)
    configPanel: {
      bg: 'white',
      bottom: 0,
      display: {base: 'none', lg: 'flex'},
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'fixed',
      right: 0,
      top: 0,
      zIndex: 1100,
    },

    // Main Flex Container — object syntax per project standards
    mainFlexContainer: {
      direction: {base: 'column', lg: 'row'},
      gap: {base: '0', lg: '80px'},
    },

    // Base Information Sections — object syntax per project standards
    mobileBaseInfoSection: {
      display: {base: 'block', lg: 'none'},
      order: 1,
    },

    desktopBaseInfoSection: {
      display: {base: 'none', lg: 'block'},
    },

    baseInformationStack: {
      align: 'flex-start',
      gap: 6,
      marginBottom: {base: 6, lg: 0},
      marginRight: {base: 0, lg: 4},
      width: '100%',
    },

    baseInformationProductPackage: {
      align: 'flex-start',
      gap: {base: 4, lg: 6},
      width: '100%',
    },

    baseInformationProductInfo: {
      align: 'flex-start',
      gap: 0,
      width: '100%',
    },

    baseInformationTitle: {
      color: '#2A2A2A',
      fontFamily: 'body',
      fontSize: {base: '24px', lg: '32px'},
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: {base: '150%', lg: '140%'},
      width: '100%',
    },

    baseInformationShortDescriptionSkeleton: {
      marginTop: 2,
      width: '100%',
    },

    baseInformationShortDescription: {
      display: 'block',
      textDecoration: 'none',
      width: '100%',
      _hover: {
        textDecoration: 'none',
      },
    },

    baseInformationShortDescriptionText: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      lineHeight: '140%',
    },

    baseInformationRatings: {
      marginTop: 4,
      width: '100%',
      sx: {
        '&&:not(:has(.bv_rating_summary_buttons_container))': {
          display: 'none',
        },
      },
    },

    baseInformationDescription: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontSize: {base: 'sm', lg: 'md'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.4,
      width: '100%',
      sx: {
        '& .amb-markup, & .amb-markup p, & p': {
          color: 'grayAlpha.90',
          fontFamily: 'body',
          fontSize: {base: 'sm', lg: 'md'},
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 1.4,
        },
        '& a': {
          color: 'brandColors.primaryDarkLinen',
          fontSize: 'xs',
          fontWeight: 600,
        },
      },
    },

    // Variations Section — lg+: rail uses flex-basis (width is ignored when basis is 0)
    variationsSection: {
      align: 'stretch',
      flex: {base: 1, lg: `0 0 ${PDP_RAIL_WIDTH_LG}`},
      flexBasis: {base: '100%', lg: PDP_RAIL_WIDTH_LG},
      maxWidth: {base: '100%', lg: PDP_RAIL_WIDTH_LG},
      minWidth: {base: 0, lg: PDP_RAIL_WIDTH_LG},
      width: {base: '100%', lg: PDP_RAIL_WIDTH_LG},
      order: 3,
      spacing: 8,
      paddingRight: 0,
    },

    // Tabs Container
    tabsContainer: {
      marginTop: {base: '24px', lg: '32px'},
    },

    // Quantity Label
    quantityLabel: {
      fontSize: 'md',
      fontWeight: 'medium',
    },

    // Full Details Link
    fullDetailsLink: {
      color: 'blue.600',
    },

    // Promotion accordion — Figma Offer option (11183:79584 desktop / 11183:74838 mobile)
    promotionAccordion: {
      mt: 4,
      width: '100%',
    },

    /** Finished good promo block — above What's included; Figma section divider + 56/64 spacing. */
    finishedGoodPromotion: {
      borderTop: 'none',
      borderBottom: '1px solid',
      borderColor: 'brandColors.greyscale10',
      paddingTop: {base: '16px', lg: '16px'},
      paddingBottom: {base: '56px', lg: '64px'},
      marginBottom: {base: '40px', lg: '48px'},
      width: '100%',
      sx: {
        '& .product-view-promotion-accordion': {
          mt: 0,
        },
        '& + .finished-good-view': {
          marginTop: 0,
        },
      },
    },

    promotionAccordionItem: {
      bg: 'white',
      border: 'none',
      borderRadius: 'base',
      overflow: 'hidden',
    },

    promotionAccordionButton: {
      alignItems: 'center',
      bg: 'white',
      borderRadius: 'base',
      display: 'flex',
      h: 'auto',
      justifyContent: 'center',
      minH: 'unset',
      minW: 'unset',
      p: 4,
      w: '100%',
      _expanded: {
        bg: 'white',
      },
      _hover: {
        bg: 'white',
      },
      _focus: A11Y_FOCUS_VISIBLE._focus,
      _focusVisible: {
        ...A11Y_FOCUS_VISIBLE._focusVisible,
        // Inset ring — parent `.promotionAccordionItem` uses overflow:hidden.
        boxShadow: 'inset 0 0 0 3px #1B96FF',
      },
    },

    promotionButtonInner: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: 4,
      minW: 0,
      width: '100%',
    },

    promotionContent: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: 3,
      minW: 0,
    },

    promotionDot: {
      bg: 'secondary.newSaleRed',
      borderRadius: '50%',
      flexShrink: 0,
      height: '9px',
      width: '9px',
    },

    promotionTitle: {
      color: 'greyscale.100',
      flex: 1,
      fontFamily: 'body',
      fontSize: 'md',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: {base: 1.4, lg: 1.6},
      textAlign: 'left',
    },

    promotionToggleBox: {
      alignItems: 'center',
      border: '1px solid',
      borderColor: 'greyscale.100',
      borderRadius: 'base',
      display: 'flex',
      flexShrink: 0,
      h: '16px',
      justifyContent: 'center',
      minH: '16px',
      minW: '16px',
      p: 0,
      w: '16px',
    },

    promotionToggleIcon: {
      boxSize: '10px',
      color: 'gray.900',
    },

    promotionAccordionPanel: {
      pb: 4,
      pt: 0,
      px: 4,
    },

    // Error Message
    errorMessage: {
      color: 'orange.600',
      fontWeight: 600,
      marginBottom: 8,
    },

    // Stock Indicator
    stockIndicatorFlex: {
      align: 'center',
      gap: 2,
    },

    stockIndicatorBox: {
      height: '16px',
      position: 'relative',
      width: '16px',
    },

    stockIndicatorBreathingDot: {
      backgroundColor: 'green.500',
      borderRadius: '50%',
      height: '8px',
      left: '50%',
      opacity: 0.3,
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '8px',
      zIndex: 1,
      sx: {
        '@keyframes breathing': {
          '0%': {
            opacity: 0.3,
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '50%': {
            opacity: 0.1,
            transform: 'translate(-50%, -50%) scale(1.8)',
          },
          '100%': {
            opacity: 0.3,
            transform: 'translate(-50%, -50%) scale(1)',
          },
        },
        'animation': 'breathing 2s ease-in-out infinite',
      },
    },

    stockIndicatorSolidDot: {
      backgroundColor: 'green.500',
      borderRadius: '50%',
      height: '8px',
      left: '50%',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '8px',
      zIndex: 2,
    },

    stockIndicatorText: {
      color: 'gray.800',
      fontSize: 'md',
      fontWeight: 'normal',
    },

    // Out of Stock Indicator (Finished Good products only)
    outOfStockIndicatorFlex: {
      align: 'center',
      gap: 2,
    },

    outOfStockIndicatorBox: {
      height: '16px',
      position: 'relative',
      width: '16px',
    },

    outOfStockIndicatorDot: {
      backgroundColor: 'brandColors.error',
      borderRadius: '50%',
      height: '8px',
      left: '50%',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '8px',
    },

    outOfStockIndicatorText: {
      color: 'gray.800',
      fontSize: 'md',
      fontWeight: 'normal',
    },

    // Notify Me Button (Finished Good out-of-stock products only)
    notifyMeButton: {
      marginBottom: 4,
      variant: 'outlined-light',
      width: '100%',
    },

    // Total Price (Figma: Total price / Price Info)
    pdpPriceSection: {
      width: '100%',
    },

    pdpPaymentMethods: {
      className: 'pdp-payment-methods',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
    },

    productActionSectionMessaging: {
      className: 'product-action-section-messaging',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      sx: {
        'borderBottom': '1px solid',
        'borderBottomColor': 'grayAlpha.10',
        'borderTop': '1px solid',
        'borderTopColor': 'grayAlpha.10',
        'marginTop': 2,
        '& > .pdp-payment-methods': {
          paddingBottom: '16px',
          paddingTop: '12px',
        },
        '& > .trustpilot-micro + .pdp-payment-methods': {
          borderTop: '1px solid',
          borderTopColor: 'grayAlpha.10',
        },
      },
    },

    totalPriceInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
    },

    totalPriceRow: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
    },

    totalPriceDetails: {
      alignItems: 'flex-start',
      color: '#000',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },

    totalPriceLabel: {
      color: '#000',
      fontSize: {base: '14px', lg: '12px'},
      fontWeight: 'semibold',
      lineHeight: {base: '140%', lg: '120%'},
    },

    totalPriceValue: {
      color: '#000',
      fontSize: {base: '20px', lg: '24px'},
      fontWeight: 'medium',
      lineHeight: {base: '150%', lg: '140%'},
    },

    totalPriceSavingBadge: {
      alignSelf: 'flex-start',
      bg: 'secondary.summerRed',
      borderRadius: '4px',
      color: 'text.white',
      display: 'inline-flex',
      fontSize: {base: '14px', lg: '12px'},
      fontWeight: 'semibold',
      lineHeight: {base: '140%', lg: '120%'},
      px: '8px',
      py: '4px',
    },

    totalPricePackageInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      width: '100%',
    },

    totalPriceListValue: {
      color: 'greyscale.70',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: {base: '140%', lg: '120%'},
      sx: {
        '&&&': {
          fontSize: '14px',
          lineHeight: 1.2,
        },
      },
    },

    // Full / list price amount — no strikethrough (client sign-off).
    totalPriceListValueAmount: {
      textDecoration: 'none',
      sx: {
        '&&&': {
          fontSize: '14px',
          lineHeight: 1.2,
        },
      },
    },

    totalPriceDeposit: {
      className: 'pdp-deposit-line',
      color: 'greyscale.70',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: {base: 'normal', lg: 'semibold'},
      lineHeight: '140%',
    },

    totalPricePaymentLogos: {
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
      height: '15px',
    },

    // Inventory Message
    inventoryMessage: {
      color: 'orange.600',
      fontWeight: 600,
      marginBottom: 8,
    },

    // Terms and Conditions
    termsAndConditions: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
      mt: {base: '56px', lg: '64px'},
      sx: {
        '& a': {color: 'brandColors.primaryDarkLinen', textDecoration: 'underline'},
        '& p': {marginBottom: 1},
      },
    },

    // Sticky Action Buttons (Mobile)
    stickyActionButtons: {
      bg: 'white',
      bottom: 0,
      left: 0,
      p: [4, 4, 6],
      position: 'fixed',
      width: '100%',
      zIndex: 2,
    },

    // Action Buttons (Figma: Total price → Actions → Add to cart)
    actionButton: {
      alignItems: 'center',
      bg: 'brandColors.greyscale',
      borderColor: 'brandColors.greyscale',
      borderRadius: '4px',
      borderStyle: 'solid',
      borderWidth: '1px',
      color: 'text.white',
      display: 'flex',
      fontFamily: 'body',
      fontSize: '16px',
      fontWeight: 600,
      gap: '8px',
      h: '54px',
      height: '54px',
      justifyContent: 'center',
      lineHeight: '140%',
      minH: 'unset',
      minW: 'unset',
      px: '48px',
      py: '16px',
      textAlign: 'center',
      width: '100%',
      sx: {
        fontVariantNumeric: 'lining-nums proportional-nums',
      },
      _hover: {
        bg: 'white',
        borderColor: 'brandColors.greyscale',
        color: 'brandColors.greyscale',
        _disabled: {
          bg: 'brandColors.greyscale10',
          border: 'none',
          color: 'gray.400',
        },
      },
      _active: {
        bg: 'brandColors.greyscale10',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'brandColors.greyscale10',
        border: 'none',
        color: 'gray.400',
        cursor: 'not-allowed',
        fontWeight: '600',
        opacity: 1,
        _hover: {
          bg: 'brandColors.greyscale10',
          color: 'gray.400',
        },
      },
    },

    wishlistButton: {
      marginBottom: 4,
      variant: 'outlined-light',
      width: '100%',
    },
    specificationsDimensionImageBox: {
      alignSelf: {base: 'center', lg: 'flex-start'},
      flex: {base: 'none', lg: '0 0 42.96875%'},
      maxWidth: {base: '100%', lg: '42.96875%'},
    },

    // ── Add to Cart Strip ──

    addToCartStripDesktop: {
      alignItems: 'center',
      bg: 'white',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      bottom: 0,
      display: {base: 'none', lg: 'flex'},
      left: 0,
      paddingX: 20,
      paddingY: 3,
      position: 'fixed',
      right: 0,
      width: '100%',
      zIndex: 'sticky',
    },

    addToCartStripDesktopInner: {
      alignItems: 'center',
      alignSelf: 'stretch',
      display: 'flex',
      gap: '24px',
      marginX: 'auto',
      maxWidth: 'container.xxl',
      paddingX: '48px',
      paddingY: '12px',
      width: '100%',
      minHeight: '78px',
    },

    addToCartStripMobile: {
      alignItems: 'center',
      justifyContent: 'center',
      bg: 'white',
      borderLeft: '1px solid',
      borderRight: '1px solid',
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      bottom: 0,
      display: {base: 'flex', lg: 'none'},
      left: 0,
      position: 'fixed',
      right: 0,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      width: '100%',
      zIndex: 'sticky',
    },

    addToCartStripMobileInner: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      paddingX: '20px',
      paddingY: '12px',
      width: '100%',
      maxWidth: '100%',
    },

    addToCartStripProductName: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      sx: {
        fontVariantNumeric: 'lining-nums proportional-nums',
      },
    },

    addToCartStripOptions: {
      alignSelf: 'stretch',
      color: 'brandColors.greyscale80',
      fontFamily: 'body',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginTop: 0.5,
      sx: {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        display: '-webkit-box',
      },
    },

    addToCartStripPrice: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '140%',
      whiteSpace: 'nowrap',
      sx: {
        fontVariantNumeric: 'lining-nums proportional-nums',
      },
    },

    addToCartStripButton: {
      alignItems: 'center',
      bg: 'brandColors.greyscale',
      borderColor: 'brandColors.greyscale',
      borderRadius: '4px',
      borderStyle: 'solid',
      borderWidth: '1px',
      color: 'white',
      display: 'flex',
      fontFamily: 'body',
      fontSize: '1rem',
      fontWeight: 600,
      gap: '8px',
      h: '54px',
      justifyContent: 'center',
      lineHeight: 1.4,
      minW: 'unset',
      px: {base: '24px', lg: '48px'},
      py: '16px',
      textAlign: 'center',
      sx: {
        fontVariantNumeric: 'lining-nums proportional-nums',
      },
      _hover: {
        bg: 'white',
        color: 'brandColors.greyscale',
        _disabled: {
          bg: 'brandColors.greyscale10',
          border: 'none',
          color: 'gray.400',
        },
      },
      _active: {
        bg: 'brandColors.greyscale10',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'brandColors.greyscale10',
        border: 'none',
        color: 'gray.400',
        cursor: 'not-allowed',
        fontWeight: '600',
        opacity: 1,
        _hover: {
          bg: 'brandColors.greyscale10',
          color: 'gray.400',
        },
      },
    },

    addToCartStripMobileTextBlock: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: '4px',
      marginRight: '4px',
      minW: 0,
    },

    addToCartStripMobileProductName: {
      alignSelf: 'stretch',
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: 1.2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      sx: {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        display: '-webkit-box',
      },
    },

    addToCartStripMobileFromPrefix: {
      color: 'brandColors.greyscale60',
      flexShrink: 0,
      fontFamily: 'body',
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.2,
      paddingRight: '4px',
    },

    /** Disabled strip row only: From + price (no chevron). */
    addToCartStripMobilePrice: {
      color: 'brandColors.greyscale60',
      flex: '1 1 auto',
      fontFamily: 'body',
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.2,
      maxW: '100%',
      minW: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },

    /** Price inside From + (price+chevron) cluster — no width:100% (avoids gap before arrow). */
    addToCartStripMobilePriceInCluster: {
      color: 'brandColors.greyscale60',
      flex: '1 1 0%',
      fontFamily: 'body',
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.2,
      minW: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    /** From | remainder — remainder holds a tight price+chevron cluster (see below). */
    addToCartStripMobilePriceRowWithChevron: {
      alignItems: 'center',
      alignSelf: 'stretch',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: 0,
      maxW: '100%',
      minW: 0,
      width: '100%',
    },

    /** Eats space after “From” so the cluster can use min(100%, max-content). */
    addToCartStripMobilePriceRemainder: {
      display: 'flex',
      flex: '1 1 auto',
      justifyContent: 'flex-start',
      maxW: '100%',
      minW: 0,
    },

    /**
     * Hugs price + arrow like design: short amounts stay compact; long amounts cap to strip width.
     * `min(100%, max-content)` avoids stretching the price cell and pushing the chevron away.
     */
    addToCartStripMobilePriceChevronCluster: {
      alignItems: 'center',
      display: 'inline-flex',
      maxW: '100%',
      minW: 0,
      sx: {
        width: 'min(100%, max-content)',
      },
    },

    addToCartStripChevron: {
      alignItems: 'center',
      bg: 'transparent',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      color: 'brandColors.viewDetailsLink',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      h: '11.144px',
      justifyContent: 'center',
      lineHeight: 0,
      minH: '11.144px',
      minW: '11.144px',
      p: 0,
      w: '11.144px',
      _focus: {
        boxShadow: 'none',
      },
      _focusVisible: {
        boxShadow: '0 0 0 2px rgba(166, 124, 74, 0.45)',
      },
      _hover: {
        bg: 'transparent',
        opacity: 0.85,
      },
    },

    productActionSection: {
      borderTop: '2px solid',
      borderColor: 'brandColors.greyscale10',
      paddingTop: {base: '56px', lg: '64px'},
    },
  },
};

export default ProductDetail;
